import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";

/**
 * Why an ordering action was refused, for a message both apps translate from
 * `orders:problems.<problem>`. See api.md, Ordering (staff).
 */
export type OrderingProblem =
	| "network"
	| "forbidden"
	| "notFound"
	/** Someone else accepted the batch first. */
	| "alreadyAccepted"
	/** Someone else rejected the batch first. */
	| "alreadyRejected"
	/** Every item was taken out: reject instead. */
	| "nothingLeft"
	/** The table got an order meanwhile (`details.orderId`): add to that one, or merge. */
	| "tableTaken"
	/** The bill is printed: void it first (Phase 4). */
	| "orderBilled"
	/** The order was cancelled or merged meanwhile. */
	| "orderClosed"
	/** An item, size or add-on sold out since it was picked. */
	| "itemUnavailable"
	/** A size or add-on no longer fits the item (the menu changed). */
	| "invalidChoice"
	/** The line was cancelled or removed already. */
	| "itemNotActive"
	/** More units than the line has. */
	| "tooMany"
	/** A takeaway has no table to move. */
	| "takeaway"
	| "failed";

export function orderingProblemOf(error: unknown): OrderingProblem {
	const code = errorCodeOf(error);
	const details = errorDetailsOf(error);
	if (code === "NETWORK") return "network";
	if (code === "FORBIDDEN") return "forbidden";
	if (code === "NOT_FOUND") return "notFound";
	if (code === "ITEM_UNAVAILABLE") return "itemUnavailable";
	if (code === "VALIDATION") {
		if (details.reason === "invalid_choice") return "invalidChoice";
		if (details.field === "quantity") return "tooMany";
	}
	if (code === "CONFLICT") {
		if (details.reason === "already_decided")
			return details.status === "rejected" ? "alreadyRejected" : "alreadyAccepted";
		if (details.reason === "nothing_left") return "nothingLeft";
		if (details.reason === "table_has_open_order") return "tableTaken";
		if (details.reason === "order_billed") return "orderBilled";
		if (details.reason === "order_closed") return "orderClosed";
		if (details.reason === "item_not_active") return "itemNotActive";
		if (details.reason === "takeaway") return "takeaway";
	}
	return "failed";
}

/** The open order a refused `createOrder` or `moveTable` points at, if any. */
export function takenByOrderOf(error: unknown): string | null {
	const details = errorDetailsOf(error);
	return details.reason === "table_has_open_order" && typeof details.orderId === "string"
		? details.orderId
		: null;
}
